import { Component, EventEmitter, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take, takeUntil } from 'rxjs/operators';
import { ConfiguredToastrService } from '../../../../helper/toastr/configured-toastr.service';
import { ProjectFile } from '../../../../models/project/project-info.model';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-attached-files',
  templateUrl: './attached-files.component.html',
  styleUrls: [
    './attached-files.component.scss',
  ]
})
export class AttachedFilesComponent extends UserpilotReloader {
  onDestroyNotifier = new EventEmitter();
  trigger: string;

  inputFile: File;
  projectFile: ProjectFile;

  allowedFileExtensions = ['csv', 'pdf', 'xlsx'];
  // 15MB file cap
  maxFileSize = 15000000;

  isEdit = false;

  showClearName = false;

  constructor(
    private http: HttpClient,
    private dialogRef: MatDialogRef<AttachedFilesComponent>,
    private toastrService: ConfiguredToastrService,
    public translationService: AnessaTranslationService,
    @Inject(MAT_DIALOG_DATA) private data: { projectId: string, file: ProjectFile },
  ) {
    super();
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });
    if (this.data.file) {
      this.isEdit = true;
      this.projectFile = this.data.file;
    } else {
      this.projectFile = new ProjectFile();
    }
  }

  clearTitle() {
    this.projectFile.name = null;
  }

  onUpload(target: HTMLInputElement) {
    const files = target.files;

    if (files && files.length) {
      const fileToUpload = files[0];

      const fileParts = fileToUpload.name.split('.');
      if (!this.allowedFileExtensions.includes(String(fileParts[fileParts.length - 1]).toLowerCase())) {
        this.toastrService.error(this.translationService.get('PROJECT.PROJECT_INFO.FILE.ERROR_TYPE') + this.allowedFileExtensions.join(','));
        return;
      }

      if (fileToUpload.size > this.maxFileSize) {
        this.toastrService.error(this.translationService.get('PROJECT.PROJECT_INFO.FILE.ERROR_SIZE', { '%d': this.maxFileSize / 1000000 }));
        return;
      }

      this.projectFile.fileSize = fileToUpload.size;
      this.projectFile.fileExt = fileParts[fileParts.length - 1];

      const formData: FormData = new FormData();
      formData.append('uploadFile', fileToUpload, fileToUpload.name);
      formData.append('size', String(fileToUpload.size));
      formData.append('extension', fileParts[fileParts.length - 1]);

      const projectId = this.data.projectId;

      this.toastrService.info(this.translationService.get('PROJECT.PROJECT_INFO.FILE.UPLOADING'));
      this.http.post(`/wsasapi/project-input/${projectId}/upload/`, formData)
        .pipe(take(1))
        .subscribe((data: { obj: string }) => {
          this.toastrService.success(this.translationService.get('PROJECT.PROJECT_INFO.FILE.UPLOADED'));
          this.projectFile.fileId = data.obj;
        });
    }
  }

  onDownload() {
    const projectId = this.data.projectId;
    const fileId = this.projectFile.fileId;
    const fileExt = this.projectFile.fileExt;

    this.http.get(`/wsasapi/project-input/${projectId}/download/${fileId}?ext=${fileExt}`, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe(result => {
        this.toastrService.success(this.translationService.get('PROJECT.PROJECT_INFO.FILE.RETRIEVED'));
        const csvFile = new Blob([result]);
        const url = window.URL.createObjectURL(csvFile);
        const link = document.createElement('a');
        link.download = `${this.projectFile.name}.${this.projectFile.fileExt}`;
        link.href = url;
        link.click();
      });
  }

  onSave() {
    this.dialogRef.close(this.projectFile);
  }

  onCancel() {
    this.dialogRef.close(null);
  }
}
